import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  View, Text, StyleSheet, TouchableOpacity, ScrollView, SafeAreaView, StatusBar,
  Image, TextInput, Animated, ActivityIndicator, Alert, Modal, Dimensions,
  KeyboardAvoidingView, Platform, Share,
} from 'react-native';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { apiFetch } from '../../utils/apiClient';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';

let DocumentPicker;
try { DocumentPicker = require('expo-document-picker'); } catch { DocumentPicker = null; }

let LinearGradient;
try { LinearGradient = require('expo-linear-gradient').LinearGradient; }
catch { LinearGradient = ({colors,style,children,...p})=><View style={[style,{backgroundColor:colors?.[0]||'#0e4e96'}]} {...p}>{children}</View>; }

const { width: SW } = Dimensions.get('window');

// ═══════════════════════════════════════════════════════════════════════════════
// API
// ═══════════════════════════════════════════════════════════════════════════════
const API_BASE = 'https://api.jobeng.ai';
const API_KEY  = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
// Profile keys (synced with ProfileScreen)
const PROF_KEYS = { PROFILE:'prof_user_profile', DASH:'prof_dashboard_data', STATS:'profileStats' };
const APPS_KEY  = 'job_applications_data';

const STEPS = [
  {id:1, title:'Personal Info',   icon:'person-outline',     desc:'Your contact details'},
  {id:2, title:'Professional',    icon:'briefcase-outline',  desc:'Experience & skills'},
  {id:3, title:'Application',     icon:'document-text-outline',desc:'Cover letter & motivation'},
  {id:4, title:'Documents',       icon:'cloud-upload-outline',desc:'Upload & review'},
];

const REFERRAL_SOURCES = ['LinkedIn','Company Website','Job Board','Referral','Recruitment Agency','Social Media','Career Fair','Other'];
const AVAILABILITY_OPTS = ['Immediately','1 week','2 weeks','1 month','2 months','3 months'];

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
export default function JobApplicationScreen({ navigation, route }) {
  // ── Extract job params ──
  const jobId = route.params?.jobId || route.params?.id || route.params?.job?.id || `job_${Date.now()}`;
  const initialJob = route.params?.job || route.params?.jobData || route.params?.item || route.params?.data || null;

  // ── State ──
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving]   = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [jobDetails, setJobDetails] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [draftId, setDraftId] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [formData, setFormData] = useState({
    firstName:'', lastName:'', email:'', phone:'',
    linkedinUrl:'', portfolioUrl:'', githubUrl:'',
    currentJobTitle:'', experience:'', education:'', skills:'',
    salaryExpectations:'', availability:'', noticePeriod:'',
    coverLetter:'', whyInterested:'', additionalInfo:'', referralSource:'',
    documents:[], agreeToTerms:false, allowContact:true, allowDataProcessing:true,
  });

  const fadeAnim = useRef(new Animated.Value(0)).current;
  const scrollRef = useRef(null);

  // ═════════════════════════════════════════════════════════════════════════════
  // INIT — Load job + pre-fill from ProfileScreen
  // ═════════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    initForm();
    Animated.timing(fadeAnim,{toValue:1,duration:600,useNativeDriver:true}).start();
  }, []);

  const initForm = async () => {
    setIsLoading(true);
    try {
      // Set job details
      if (initialJob) {
        setJobDetails(initialJob);
      } else {
        // Try API
        const r = await apiFetch(`/api/data/jobs/${jobId}`);
        if (!r.error) {
          let job = r.data || r;
          // The raw /api/data/jobs/:id returns the bare row — it has company_id
          // but not the human-readable company_name. Enrich it via a quick
          // lookup so the cover letter prompt, share text, and payload all
          // have the real employer name.
          if (job && !job.company && job.company_id) {
            try {
              const c = await apiFetch(`/api/data/company_profiles?id=${job.company_id}&limit=1`);
              const crow = Array.isArray(c) ? c[0] : (Array.isArray(c?.data) ? c.data[0] : c);
              if (crow?.company_name) {
                job = { ...job, company: crow.company_name, companyLogo: crow.logo };
              }
            } catch (_) {}
          }
          setJobDetails(job);
        } else {
          setJobDetails({ id:jobId, title:'Job Position', company:'Company', location:'Location' });
        }
      }

      // Pre-fill from ProfileScreen
      const profileRaw = await AsyncStorage.getItem(PROF_KEYS.PROFILE) || await AsyncStorage.getItem('userProfile');
      if (profileRaw) {
        const prof = JSON.parse(profileRaw);
        const p = prof?.personal || {};
        const nameParts = (p.fullName||'').split(' ');
        setFormData(prev => ({
          ...prev,
          firstName:       nameParts[0] || prev.firstName,
          lastName:        nameParts.slice(1).join(' ') || prev.lastName,
          email:           p.email       || prev.email,
          phone:           p.phone       || prev.phone,
          linkedinUrl:     p.linkedin    || prev.linkedinUrl,
          portfolioUrl:    p.website     || prev.portfolioUrl,
          githubUrl:       p.github      || prev.githubUrl,
          currentJobTitle: p.jobTitle    || prev.currentJobTitle,
          education:       (prof.education||[])[0]?.degree ? `${(prof.education||[])[0].degree} — ${(prof.education||[])[0].institution||''}` : prev.education,
          skills:          (prof.skills||[]).map(s=>s.name||s.skill_name||'').filter(Boolean).join(', ') || prev.skills,
          availability:    p.availability || prev.availability,
          noticePeriod:    p.noticePeriod || prev.noticePeriod,
          experience:      `${(prof.experience||[]).length} role${(prof.experience||[]).length!==1?'s':''}` || prev.experience,
        }));
      }

      // Also try user_data keys
      const userRaw = await AsyncStorage.getItem('user_data') || await AsyncStorage.getItem('professional_default_user_data');
      if (userRaw) {
        const u = JSON.parse(userRaw);
        const fullName = [u.first_name||'',u.last_name||''].filter(Boolean).join(' ')||u.full_name||'';
        const nameParts = fullName.split(' ');
        setFormData(prev => ({
          ...prev,
          firstName: prev.firstName || nameParts[0] || '',
          lastName:  prev.lastName  || nameParts.slice(1).join(' ') || '',
          email:     prev.email     || u.email || '',
          phone:     prev.phone     || u.phone || '',
        }));
      }

      // Check for existing draft
      const draftsRaw = await AsyncStorage.getItem(APPS_KEY);
      if (draftsRaw) {
        const drafts = JSON.parse(draftsRaw);
        const draft = (Array.isArray(drafts)?drafts:[]).find(d=>d.jobId===jobId && d.status==='draft');
        if (draft) {
          setDraftId(draft.id);
          setFormData(prev => ({ ...prev, ...draft.formData }));
          Alert.alert('Draft Found','Your previous draft for this job has been restored.');
        }
      }
    } catch(e) { console.warn('initForm:', e?.message); }
    setIsLoading(false);
  };

  // ═════════════════════════════════════════════════════════════════════════════
  // FORM HANDLERS
  // ═════════════════════════════════════════════════════════════════════════════
  const set = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]:value }));
    setFormErrors(prev => ({ ...prev, [field]:null }));
  }, []);

  const validate = useCallback((step) => {
    const e = {};
    if (step===1) {
      if (!formData.firstName.trim()) e.firstName = 'Required';
      if (!formData.lastName.trim())  e.lastName  = 'Required';
      if (!formData.email.trim())     e.email     = 'Required';
      else if (!/\S+@\S+\.\S+/.test(formData.email)) e.email = 'Invalid email';
      if (!formData.phone.trim())     e.phone     = 'Required';
    }
    if (step===2) {
      if (!formData.currentJobTitle.trim()) e.currentJobTitle = 'Required';
      if (!formData.experience.trim())      e.experience      = 'Required';
    }
    if (step===3) {
      if (!formData.coverLetter.trim())    e.coverLetter   = 'Required';
      if (formData.coverLetter.trim().length<50) e.coverLetter = 'At least 50 characters';
      if (!formData.whyInterested.trim())  e.whyInterested = 'Required';
    }
    if (step===4) {
      if (!formData.agreeToTerms) e.agreeToTerms = 'You must agree to terms';
    }
    setFormErrors(e);
    return Object.keys(e).length === 0;
  }, [formData]);

  // Pure step-validity check (does NOT call setState — safe to use in JSX).
  // Mirrors the rules in validate() above, but returns a boolean without
  // side effects so it can be referenced in render style/disabled props.
  const isStepValid = useMemo(() => {
    const step = currentStep;
    if (step === 1) {
      return formData.firstName.trim()
          && formData.lastName.trim()
          && formData.email.trim()
          && /\S+@\S+\.\S+/.test(formData.email)
          && formData.phone.trim();
    }
    if (step === 2) {
      return formData.currentJobTitle.trim() && formData.experience.trim();
    }
    if (step === 3) {
      return formData.coverLetter.trim()
          && formData.coverLetter.trim().length >= 50
          && formData.whyInterested.trim();
    }
    if (step === 4) {
      return !!formData.agreeToTerms;
    }
    return true;
  }, [currentStep, formData]);

  const handleNext = useCallback(() => {
    if (!validate(currentStep)) {
      Alert.alert('Missing Fields','Please fill in all required fields.');
      return;
    }
    if (currentStep < 4) {
      setCurrentStep(prev => prev+1);
      scrollRef.current?.scrollTo({y:0,animated:true});
      saveDraft();
    } else {
      setShowPreview(true);
    }
  }, [currentStep, validate]);

  const handlePrev = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev-1);
      scrollRef.current?.scrollTo({y:0,animated:true});
    }
  }, [currentStep]);

  // ═════════════════════════════════════════════════════════════════════════════
  // DRAFT SAVE
  // ═════════════════════════════════════════════════════════════════════════════
  const saveDraft = useCallback(async () => {
    setIsSaving(true);
    try {
      const draftsRaw = await AsyncStorage.getItem(APPS_KEY);
      let drafts = draftsRaw ? JSON.parse(draftsRaw) : [];
      if (!Array.isArray(drafts)) drafts = [];

      const id = draftId || `app_${Date.now()}_${Math.random().toString(36).slice(2,6)}`;
      const draft = {
        id, jobId, status:'draft',
        jobTitle: jobDetails?.title || '',
        company:  jobDetails?.company || '',
        location: jobDetails?.location || '',
        salary:   jobDetails?.salary || '',
        formData: { ...formData, documents: formData.documents.map(d=>({...d, uri:undefined})) },
        lastUpdated: new Date().toISOString(),
        createdAt:   draftId ? undefined : new Date().toISOString(),
      };

      const idx = drafts.findIndex(d=>d.id===id);
      if (idx>=0) drafts[idx] = { ...drafts[idx], ...draft };
      else drafts.unshift(draft);

      await AsyncStorage.setItem(APPS_KEY, JSON.stringify(drafts));
      if (!draftId) setDraftId(id);
    } catch(e) { console.warn('saveDraft:', e?.message); }
    setIsSaving(false);
  }, [draftId, jobId, jobDetails, formData]);

  // ═════════════════════════════════════════════════════════════════════════════
  // SUBMIT APPLICATION
  // ═════════════════════════════════════════════════════════════════════════════
  const submitApplication = useCallback(async () => {
    setIsSubmitting(true);
    try {
      // Submit to API
      const payload = {
        job_id: jobId,
        job_title: jobDetails?.title || '',
        company: jobDetails?.company || '',
        candidate_name: `${formData.firstName} ${formData.lastName}`.trim(),
        candidate_email: formData.email,
        candidate_phone: formData.phone,
        current_title: formData.currentJobTitle,
        experience: formData.experience,
        education: formData.education,
        skills: formData.skills,
        cover_letter: formData.coverLetter,
        why_interested: formData.whyInterested,
        salary_expectations: formData.salaryExpectations,
        availability: formData.availability,
        notice_period: formData.noticePeriod,
        linkedin_url: formData.linkedinUrl,
        portfolio_url: formData.portfolioUrl,
        github_url: formData.githubUrl,
        referral_source: formData.referralSource,
        additional_info: formData.additionalInfo,
        status: 'submitted',
        source: 'mobile_app',
        applied_date: new Date().toISOString(),
      };

      // Route through shared applyToJob → /api/recruitment/apply which writes
      // to BOTH applications (professional dashboard) AND job_applications
      // (business recruiter pipeline). Earlier single-table post missed the
      // business side entirely.
      const { applyToJob } = require('./jobsShared');
      const r = await applyToJob(payload);

      // Also save locally
      const draftsRaw = await AsyncStorage.getItem(APPS_KEY);
      let drafts = draftsRaw ? JSON.parse(draftsRaw) : [];
      if (!Array.isArray(drafts)) drafts = [];
      const id = draftId || `app_${Date.now()}`;
      const app = {
        id: r?.id || r?.data?.id || id,
        jobId, status:'submitted',
        jobTitle: jobDetails?.title, company: jobDetails?.company,
        location: jobDetails?.location, salary: jobDetails?.salary,
        jobType: jobDetails?.jobType || jobDetails?.job_type,
        matchScore: jobDetails?.matchScore || jobDetails?.match_score || 0,
        appliedDate: new Date().toISOString(),
        formData, lastUpdated: new Date().toISOString(),
        statusHistory: [{status:'submitted', date:new Date().toISOString(), note:'Application submitted'}],
      };
      const idx = drafts.findIndex(d=>d.id===draftId);
      if (idx>=0) drafts[idx] = app; else drafts.unshift(app);
      await AsyncStorage.setItem(APPS_KEY, JSON.stringify(drafts));

      // Update dashboard stats
      try {
        const statsRaw = await AsyncStorage.getItem('prof_default_stats');
        const stats = statsRaw ? JSON.parse(statsRaw) : {};
        stats.total_applications = (stats.total_applications||0) + 1;
        await AsyncStorage.setItem('prof_default_stats', JSON.stringify(stats));
      } catch {}

      setShowPreview(false);
      setShowSuccess(true);
    } catch(e) {
      Alert.alert('Error', 'Could not submit application. Please try again.');
      console.warn('submitApplication:', e?.message);
    }
    setIsSubmitting(false);
  }, [jobId, jobDetails, formData, draftId]);

  // ═════════════════════════════════════════════════════════════════════════════
  // AI COVER LETTER
  // ═════════════════════════════════════════════════════════════════════════════
  const generateAICoverLetter = useCallback(async () => {
    setAiGenerating(true);
    try {
      // Primary: use the local profile-aware generator from ProfileManagementService.
      // It pulls from the actual resume data (name, summary, recent role, skills)
      // and produces a polished letter without needing an AI API call.
      try {
        await ProfileManagementService.bootstrap();
        const profileResult = await ProfileManagementService.getProfile();
        if (profileResult.success && profileResult.data) {
          const clResult = ProfileManagementService.generateCoverLetter(
            'professional',
            {
              jobTitle: jobDetails?.title || formData.currentJobTitle || 'this position',
              company:  jobDetails?.company || '',
            },
            profileResult.data
          );
          if (clResult.success && clResult.data) {
            set('coverLetter', clResult.data);
            Alert.alert('Generated', 'Cover letter drafted from your profile. Edit as needed.');
            setAiGenerating(false);
            return;
          }
        }
      } catch (_) {}

      // Fallback: AI service
      const prompt = `Write a professional cover letter for a ${formData.currentJobTitle||'professional'} applying for ${jobDetails?.title||'this position'} at ${jobDetails?.company||'the company'}. The candidate has ${formData.experience||'several years'} of experience. Skills: ${formData.skills||'various'}. Keep it concise (150 words max), professional, and enthusiastic.`;
      const r = await apiFetch('/api/ai/career-advice',{method:'POST',body:JSON.stringify({prompt})});
      const text = r?.advice || r?.text || r?.message || '';
      if (text) {
        set('coverLetter', text);
        Alert.alert('AI Generated','Cover letter generated. Feel free to edit it.');
      } else {
        Alert.alert('AI Unavailable','Could not generate cover letter. Please write one manually.');
      }
    } catch {
      Alert.alert('Error','AI service unavailable. Please write your cover letter manually.');
    }
    setAiGenerating(false);
  }, [formData, jobDetails, set]);

  // ═════════════════════════════════════════════════════════════════════════════
  // DOCUMENT PICKER
  // ═════════════════════════════════════════════════════════════════════════════
  const pickDocument = useCallback(async () => {
    if (!DocumentPicker) { Alert.alert('Install','Run: npx expo install expo-document-picker'); return; }
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type:['application/pdf','application/msword','application/vnd.openxmlformats-officedocument.wordprocessingml.document','image/*'],
        copyToCacheDirectory:true,
      });
      if (!result.canceled && result.assets?.[0]) {
        const doc = result.assets[0];
        set('documents', [...formData.documents, {
          id:`doc_${Date.now()}`, name:doc.name, uri:doc.uri, type:doc.mimeType,
          size:doc.size, addedAt:new Date().toISOString(),
        }]);
      }
    } catch(e) { Alert.alert('Error','Could not pick document.'); }
  }, [formData.documents, set]);

  const removeDoc = useCallback((id) => {
    set('documents', formData.documents.filter(d=>d.id!==id));
  }, [formData.documents, set]);

  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024*1024) return `${(bytes/1024).toFixed(1)} KB`;
    return `${(bytes/(1024*1024)).toFixed(1)} MB`;
  };

  // ═════════════════════════════════════════════════════════════════════════════
  // SHARE
  // ═════════════════════════════════════════════════════════════════════════════
  const shareApplication = useCallback(async () => {
    try {
      await Share.share({
        title:`Application for ${jobDetails?.title}`,
        message:`I just applied for ${jobDetails?.title} at ${jobDetails?.company}! ${jobDetails?.location||''}`,
      });
    } catch {}
  }, [jobDetails]);

  // ═════════════════════════════════════════════════════════════════════════════
  // RENDER: INPUT FIELD
  // ═════════════════════════════════════════════════════════════════════════════
  const Field = ({ label, field, placeholder, multiline, lines, keyboardType, autoCapitalize, required, maxLength }) => (
    <View style={s.fieldWrap}>
      <Text style={s.fieldLabel}>{label}{required&&<Text style={{color:'#EF4444'}}> *</Text>}</Text>
      <TextInput
        style={[s.input, multiline&&{height:Math.max(80,lines*20),textAlignVertical:'top'}, formErrors[field]&&{borderColor:'#EF4444'}]}
        value={formData[field]}
        onChangeText={v=>set(field,v)}
        placeholder={placeholder}
        placeholderTextColor="#94A3B8"
        multiline={multiline}
        numberOfLines={lines}
        keyboardType={keyboardType}
        autoCapitalize={autoCapitalize||'sentences'}
        maxLength={maxLength}
      />
      {formErrors[field] && <Text style={s.errorText}>{formErrors[field]}</Text>}
      {maxLength && multiline && <Text style={s.charCount}>{(formData[field]||'').length}/{maxLength}</Text>}
    </View>
  );

  // ═════════════════════════════════════════════════════════════════════════════
  // RENDER: STEPS
  // ═════════════════════════════════════════════════════════════════════════════
  const renderStepIndicator = () => (
    <View style={s.stepBar}>
      {STEPS.map((step,i)=>(
        <React.Fragment key={step.id}>
          <TouchableOpacity style={s.stepDot} onPress={()=>{ if(step.id<currentStep) setCurrentStep(step.id); }}>
            <View style={[s.stepCircle, currentStep>=step.id&&s.stepCircleActive, currentStep===step.id&&s.stepCircleCurrent]}>
              {currentStep>step.id
                ? <Ionicons name="checkmark" size={14} color="#FFF"/>
                : <Text style={[s.stepNum, currentStep>=step.id&&{color:'#FFF'}]}>{step.id}</Text>
              }
            </View>
            <Text style={[s.stepLabel, currentStep>=step.id&&{color:'#0e4e96',fontWeight:'700'}]}>{step.title}</Text>
          </TouchableOpacity>
          {i<3&&<View style={[s.stepLine, currentStep>step.id&&s.stepLineActive]}/>}
        </React.Fragment>
      ))}
    </View>
  );

  const renderStep1 = () => (
    <View style={s.stepContent}>
      <Text style={s.stepTitle}>Personal Information</Text>
      <Text style={s.stepDesc}>We've pre-filled from your profile. Verify and update if needed.</Text>
      <View style={{flexDirection:'row',gap:10}}>
        <View style={{flex:1}}><Field label="First Name" field="firstName" placeholder="John" required/></View>
        <View style={{flex:1}}><Field label="Last Name" field="lastName" placeholder="Smith" required/></View>
      </View>
      <Field label="Email" field="email" placeholder="john@example.com" keyboardType="email-address" autoCapitalize="none" required/>
      <Field label="Phone" field="phone" placeholder="+44 7700 900000" keyboardType="phone-pad" required/>
      <Field label="LinkedIn" field="linkedinUrl" placeholder="https://linkedin.com/in/..." autoCapitalize="none"/>
      <Field label="Portfolio / Website" field="portfolioUrl" placeholder="https://..." autoCapitalize="none"/>
      <Field label="GitHub" field="githubUrl" placeholder="https://github.com/..." autoCapitalize="none"/>
    </View>
  );

  const renderStep2 = () => (
    <View style={s.stepContent}>
      <Text style={s.stepTitle}>Professional Background</Text>
      <Text style={s.stepDesc}>Tell us about your experience and qualifications.</Text>
      <Field label="Current / Most Recent Job Title" field="currentJobTitle" placeholder="e.g. Senior Engineer" required/>
      <Field label="Years of Experience" field="experience" placeholder="e.g. 5 years" required/>
      <Field label="Education" field="education" placeholder="e.g. BSc Computer Science — UCL"/>
      <Field label="Key Skills" field="skills" placeholder="React Native, JavaScript, Python, AWS..." multiline lines={3}/>
      <Field label="Salary Expectations" field="salaryExpectations" placeholder="e.g. £65,000 — £80,000"/>
      <Text style={s.fieldLabel}>Availability / Notice Period</Text>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{marginBottom:16}}>
        <View style={{flexDirection:'row',gap:8,paddingVertical:4}}>
          {AVAILABILITY_OPTS.map(opt=>(
            <TouchableOpacity key={opt} style={[s.chip, formData.availability===opt&&s.chipActive]}
              onPress={()=>set('availability',opt)}>
              <Text style={[s.chipT, formData.availability===opt&&{color:'#FFF'}]}>{opt}</Text>
            </TouchableOpacity>
          ))}
        </View>
      </ScrollView>
    </View>
  );

  const renderStep3 = () => (
    <View style={s.stepContent}>
      <Text style={s.stepTitle}>Application Details</Text>
      <Text style={s.stepDesc}>Share your motivation for this role.</Text>

      {/* AI Cover Letter */}
      <View style={{flexDirection:'row',justifyContent:'space-between',alignItems:'center',marginBottom:6}}>
        <Text style={s.fieldLabel}>Cover Letter <Text style={{color:'#EF4444'}}>*</Text></Text>
        <TouchableOpacity style={s.aiBtn} onPress={generateAICoverLetter} disabled={aiGenerating}>
          {aiGenerating
            ? <ActivityIndicator size={12} color="#7C3AED"/>
            : <MaterialCommunityIcons name="robot" size={14} color="#7C3AED"/>
          }
          <Text style={s.aiBtnT}>{aiGenerating?'Generating...':'AI Generate'}</Text>
        </TouchableOpacity>
      </View>
      <TextInput
        style={[s.input,{height:160,textAlignVertical:'top'},formErrors.coverLetter&&{borderColor:'#EF4444'}]}
        value={formData.coverLetter}
        onChangeText={v=>set('coverLetter',v)}
        placeholder="Write a compelling cover letter..."
        placeholderTextColor="#94A3B8"
        multiline numberOfLines={8}
        maxLength={2000}
      />
      {formErrors.coverLetter && <Text style={s.errorText}>{formErrors.coverLetter}</Text>}
      <Text style={s.charCount}>{(formData.coverLetter||'').length}/2000</Text>

      <Field label="Why are you interested in this role?" field="whyInterested" placeholder="What attracts you to this position..." multiline lines={4} required maxLength={1000}/>
      <Field label="Additional Information" field="additionalInfo" placeholder="Anything else you'd like to share..." multiline lines={3} maxLength={500}/>

      <Text style={s.fieldLabel}>How did you hear about this position?</Text>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{marginBottom:16}}>
        <View style={{flexDirection:'row',gap:8,paddingVertical:4}}>
          {REFERRAL_SOURCES.map(src=>(
            <TouchableOpacity key={src} style={[s.chip, formData.referralSource===src&&s.chipActive]}
              onPress={()=>set('referralSource',src)}>
              <Text style={[s.chipT, formData.referralSource===src&&{color:'#FFF'}]}>{src}</Text>
            </TouchableOpacity>
          ))}
        </View>
      </ScrollView>
    </View>
  );

  const renderStep4 = () => (
    <View style={s.stepContent}>
      <Text style={s.stepTitle}>Documents & Review</Text>
      <Text style={s.stepDesc}>Upload your CV/resume and review before submitting.</Text>

      {/* Upload */}
      <TouchableOpacity style={s.uploadBtn} onPress={pickDocument}>
        <Ionicons name="cloud-upload-outline" size={28} color="#0e4e96"/>
        <Text style={s.uploadBtnT}>Upload CV / Resume / Documents</Text>
        <Text style={{fontSize:11,color:'#94A3B8',marginTop:2}}>PDF, Word, or Image — Max 10MB</Text>
      </TouchableOpacity>

      {formData.documents.map(doc=>(
        <View key={doc.id} style={s.docRow}>
          <Ionicons name="document-text" size={20} color="#0e4e96"/>
          <View style={{flex:1,marginLeft:10}}>
            <Text style={{fontSize:13,fontWeight:'600',color:'#1F2937'}} numberOfLines={1}>{doc.name}</Text>
            <Text style={{fontSize:11,color:'#94A3B8'}}>{formatSize(doc.size)}</Text>
          </View>
          <TouchableOpacity onPress={()=>removeDoc(doc.id)} hitSlop={{top:8,bottom:8,left:8,right:8}}>
            <Ionicons name="trash-outline" size={18} color="#EF4444"/>
          </TouchableOpacity>
        </View>
      ))}

      {/* Checkboxes */}
      <View style={{marginTop:20}}>
        {[
          {field:'agreeToTerms',    label:'I agree to the terms and conditions and privacy policy *', required:true},
          {field:'allowContact',    label:'Allow the company to contact me about future opportunities'},
          {field:'allowDataProcessing',label:'I consent to my data being processed for recruitment purposes'},
        ].map(({field,label,required})=>(
          <TouchableOpacity key={field} style={s.checkRow} onPress={()=>set(field,!formData[field])}>
            <Ionicons name={formData[field]?'checkbox':'square-outline'} size={22} color={formData[field]?'#0e4e96':'#94A3B8'}/>
            <Text style={[s.checkLabel, required&&formErrors[field]&&{color:'#EF4444'}]}>{label}</Text>
          </TouchableOpacity>
        ))}
      </View>
    </View>
  );

  // ═════════════════════════════════════════════════════════════════════════════
  // RENDER: NAV BUTTONS
  // ═════════════════════════════════════════════════════════════════════════════
  const renderNav = () => (
    <View style={s.navBar}>
      {currentStep>1 ? (
        <TouchableOpacity style={s.prevBtn} onPress={handlePrev}>
          <Ionicons name="chevron-back" size={18} color="#0e4e96"/>
          <Text style={s.prevBtnT}>Back</Text>
        </TouchableOpacity>
      ) : <View style={{width:80}}/>}
      <Text style={s.navStep}>Step {currentStep}/4</Text>
      <TouchableOpacity style={[s.nextBtn, !isStepValid && {opacity:0.5}]} onPress={handleNext}>
        <Text style={s.nextBtnT}>{currentStep===4?'Review':'Next'}</Text>
        <Ionicons name="chevron-forward" size={18} color="#FFF"/>
      </TouchableOpacity>
    </View>
  );

  // ═════════════════════════════════════════════════════════════════════════════
  // RENDER: PREVIEW MODAL
  // ═════════════════════════════════════════════════════════════════════════════
  const PreviewSection = ({title, children}) => (
    <View style={{marginBottom:20}}>
      <Text style={{fontSize:15,fontWeight:'700',color:'#1F2937',marginBottom:8}}>{title}</Text>
      {children}
    </View>
  );

  const PreviewRow = ({label, value}) => (
    value ? <Text style={{fontSize:13,color:'#475569',marginBottom:4}}><Text style={{fontWeight:'600',color:'#1F2937'}}>{label}: </Text>{value}</Text> : null
  );

  const renderPreviewModal = () => (
    <Modal visible={showPreview} animationType="slide" presentationStyle="pageSheet" onRequestClose={()=>setShowPreview(false)}>
      <SafeAreaView style={{flex:1,backgroundColor:'#FFF'}}>
        <View style={s.modalHdr}>
          <TouchableOpacity onPress={()=>setShowPreview(false)}><Ionicons name="close" size={24} color="#1F2937"/></TouchableOpacity>
          <Text style={{fontSize:17,fontWeight:'700',color:'#1F2937'}}>Review Application</Text>
          <View style={{width:24}}/>
        </View>
        <ScrollView style={{flex:1,padding:20}} showsVerticalScrollIndicator={false}>
          <PreviewSection title="Applying For">
            <View style={{backgroundColor:'#F8FAFC',borderRadius:12,padding:14,borderWidth:1,borderColor:'#E2E8F0'}}>
              <Text style={{fontSize:16,fontWeight:'700',color:'#1F2937'}}>{jobDetails?.title}</Text>
              <Text style={{fontSize:13,color:'#64748B',marginTop:2}}>{jobDetails?.company} • {jobDetails?.location}</Text>
              {jobDetails?.salary&&<Text style={{fontSize:13,fontWeight:'700',color:'#10b981',marginTop:4}}>{jobDetails.salary}</Text>}
            </View>
          </PreviewSection>
          <PreviewSection title="Personal Information">
            <PreviewRow label="Name" value={`${formData.firstName} ${formData.lastName}`}/>
            <PreviewRow label="Email" value={formData.email}/>
            <PreviewRow label="Phone" value={formData.phone}/>
            <PreviewRow label="LinkedIn" value={formData.linkedinUrl}/>
            <PreviewRow label="Portfolio" value={formData.portfolioUrl}/>
          </PreviewSection>
          <PreviewSection title="Professional">
            <PreviewRow label="Title" value={formData.currentJobTitle}/>
            <PreviewRow label="Experience" value={formData.experience}/>
            <PreviewRow label="Education" value={formData.education}/>
            <PreviewRow label="Skills" value={formData.skills}/>
            <PreviewRow label="Salary" value={formData.salaryExpectations}/>
            <PreviewRow label="Availability" value={formData.availability}/>
          </PreviewSection>
          <PreviewSection title="Cover Letter">
            <Text style={{fontSize:13,color:'#475569',lineHeight:20}}>{formData.coverLetter}</Text>
          </PreviewSection>
          <PreviewSection title="Motivation">
            <Text style={{fontSize:13,color:'#475569',lineHeight:20}}>{formData.whyInterested}</Text>
          </PreviewSection>
          {formData.documents.length>0&&(
            <PreviewSection title={`Documents (${formData.documents.length})`}>
              {formData.documents.map(d=><Text key={d.id} style={{fontSize:13,color:'#475569',marginBottom:2}}>• {d.name}</Text>)}
            </PreviewSection>
          )}
        </ScrollView>
        <View style={{padding:16,borderTopWidth:1,borderTopColor:'#E2E8F0',gap:10}}>
          <TouchableOpacity style={s.submitBtn} onPress={submitApplication} disabled={isSubmitting}>
            {isSubmitting ? <ActivityIndicator color="#FFF"/> : <>
              <Text style={s.submitBtnT}>Submit Application</Text>
              <Ionicons name="send" size={18} color="#FFF"/>
            </>}
          </TouchableOpacity>
          <TouchableOpacity style={s.editBtn} onPress={()=>setShowPreview(false)}>
            <Text style={s.editBtnT}>Edit Application</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    </Modal>
  );

  // ═════════════════════════════════════════════════════════════════════════════
  // RENDER: SUCCESS MODAL
  // ═════════════════════════════════════════════════════════════════════════════
  const renderSuccessModal = () => (
    <Modal visible={showSuccess} animationType="fade" transparent onRequestClose={()=>{}}>
      <View style={s.successOverlay}>
        <View style={s.successCard}>
          <View style={{width:72,height:72,borderRadius:36,backgroundColor:'#D1FAE5',justifyContent:'center',alignItems:'center',marginBottom:16}}>
            <Ionicons name="checkmark-circle" size={48} color="#10b981"/>
          </View>
          <Text style={{fontSize:22,fontWeight:'800',color:'#1F2937',textAlign:'center',marginBottom:8}}>Application Submitted!</Text>
          <Text style={{fontSize:14,color:'#64748B',textAlign:'center',lineHeight:20,marginBottom:24}}>
            Your application for <Text style={{fontWeight:'700'}}>{jobDetails?.title}</Text> at <Text style={{fontWeight:'700'}}>{jobDetails?.company}</Text> has been submitted.
          </Text>
          <TouchableOpacity style={[s.submitBtn,{marginBottom:10}]} onPress={()=>{setShowSuccess(false);navigation.navigate('JobApplications');}}>
            <Text style={s.submitBtnT}>View My Applications</Text>
          </TouchableOpacity>
          <TouchableOpacity style={s.editBtn} onPress={shareApplication}>
            <Ionicons name="share-social-outline" size={16} color="#0e4e96"/>
            <Text style={[s.editBtnT,{marginLeft:6}]}>Share</Text>
          </TouchableOpacity>
          <TouchableOpacity style={{marginTop:10}} onPress={()=>{setShowSuccess(false);navigation.goBack();}}>
            <Text style={{fontSize:14,color:'#64748B',fontWeight:'500'}}>Back to Job</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );

  // ═════════════════════════════════════════════════════════════════════════════
  // LOADING
  // ═════════════════════════════════════════════════════════════════════════════
  if (isLoading) {
    return (
      <View style={{flex:1,justifyContent:'center',alignItems:'center',backgroundColor:'#F8FAFC'}}>
        <ActivityIndicator size={36} color="#0e4e96"/>
        <Text style={{marginTop:12,fontSize:14,color:'#64748B'}}>Loading application form...</Text>
      </View>
    );
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // MAIN RENDER
  // ═════════════════════════════════════════════════════════════════════════════
  return (
    <SafeAreaView style={{flex:1,backgroundColor:'#F8FAFC'}}>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96"/>
      <LinearGradient colors={['#0e4e96','#1e7be2']} style={s.header}>
        <TouchableOpacity style={s.hdrBtn} onPress={()=>navigation.goBack()}>
          <Ionicons name="arrow-back" size={22} color="#FFF"/>
        </TouchableOpacity>
        <View style={{flex:1,marginLeft:10}}>
          <Text style={s.hdrTitle}>Apply for Position</Text>
          <Text style={s.hdrSub} numberOfLines={1}>{jobDetails?.title} — {jobDetails?.company}</Text>
        </View>
        {isSaving && <ActivityIndicator size={18} color="#FFF" style={{marginRight:4}}/>}
        <TouchableOpacity style={s.hdrBtn} onPress={saveDraft}>
          <Ionicons name="save-outline" size={20} color="#FFF"/>
        </TouchableOpacity>
      </LinearGradient>

      <Animated.View style={{flex:1,opacity:fadeAnim}}>
        {renderStepIndicator()}
        <KeyboardAvoidingView style={{flex:1}} behavior={Platform.OS==='ios'?'padding':undefined}>
          <ScrollView ref={scrollRef} style={{flex:1}} contentContainerStyle={{paddingBottom:20}} showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled">
            {currentStep===1 && renderStep1()}
            {currentStep===2 && renderStep2()}
            {currentStep===3 && renderStep3()}
            {currentStep===4 && renderStep4()}
          </ScrollView>
        </KeyboardAvoidingView>
        {renderNav()}
      </Animated.View>

      {renderPreviewModal()}
      {renderSuccessModal()}
    </SafeAreaView>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// STYLES
// ═══════════════════════════════════════════════════════════════════════════════
const s = StyleSheet.create({
  header:      {flexDirection:'row',alignItems:'center',paddingHorizontal:16,paddingVertical:14},
  hdrBtn:      {width:38,height:38,borderRadius:12,backgroundColor:'rgba(255,255,255,0.18)',justifyContent:'center',alignItems:'center'},
  hdrTitle:    {fontSize:18,fontWeight:'700',color:'#FFF'},
  hdrSub:      {fontSize:12,color:'rgba(255,255,255,0.75)',marginTop:1},
  stepBar:     {flexDirection:'row',alignItems:'flex-start',justifyContent:'center',paddingVertical:16,paddingHorizontal:12,backgroundColor:'#FFF',borderBottomWidth:1,borderBottomColor:'#E2E8F0'},
  stepDot:     {alignItems:'center',width:60},
  stepCircle:  {width:30,height:30,borderRadius:15,backgroundColor:'#E2E8F0',justifyContent:'center',alignItems:'center'},
  stepCircleActive:{backgroundColor:'#0e4e96'},
  stepCircleCurrent:{backgroundColor:'#0e4e96',borderWidth:3,borderColor:'rgba(14,78,150,0.25)'},
  stepNum:     {fontSize:12,fontWeight:'700',color:'#94A3B8'},
  stepLabel:   {fontSize:9,color:'#94A3B8',fontWeight:'500',marginTop:4,textAlign:'center'},
  stepLine:    {width:20,height:2,backgroundColor:'#E2E8F0',marginTop:14,marginHorizontal:2},
  stepLineActive:{backgroundColor:'#0e4e96'},
  stepContent: {padding:20,backgroundColor:'#FFF',marginBottom:1},
  stepTitle:   {fontSize:22,fontWeight:'800',color:'#1F2937',marginBottom:4},
  stepDesc:    {fontSize:13,color:'#64748B',marginBottom:20},
  fieldWrap:   {marginBottom:16},
  fieldLabel:  {fontSize:13,fontWeight:'700',color:'#1F2937',marginBottom:6},
  input:       {borderWidth:1,borderColor:'#E2E8F0',borderRadius:12,paddingHorizontal:14,paddingVertical:12,fontSize:14,color:'#1F2937',backgroundColor:'#F8FAFC'},
  errorText:   {fontSize:11,color:'#EF4444',fontWeight:'500',marginTop:3},
  charCount:   {fontSize:10,color:'#94A3B8',textAlign:'right',marginTop:2},
  chip:        {paddingHorizontal:14,paddingVertical:8,borderRadius:20,borderWidth:1,borderColor:'#E2E8F0',backgroundColor:'#F8FAFC'},
  chipActive:  {backgroundColor:'#0e4e96',borderColor:'#0e4e96'},
  chipT:       {fontSize:12,fontWeight:'600',color:'#475569'},
  aiBtn:       {flexDirection:'row',alignItems:'center',gap:4,backgroundColor:'#F5F3FF',borderRadius:8,paddingHorizontal:10,paddingVertical:6,borderWidth:1,borderColor:'#DDD6FE'},
  aiBtnT:      {fontSize:11,fontWeight:'700',color:'#7C3AED'},
  uploadBtn:   {borderWidth:2,borderColor:'#0e4e96',borderStyle:'dashed',borderRadius:14,paddingVertical:24,alignItems:'center',marginBottom:16},
  uploadBtnT:  {fontSize:14,fontWeight:'600',color:'#0e4e96',marginTop:6},
  docRow:      {flexDirection:'row',alignItems:'center',padding:12,backgroundColor:'#F8FAFC',borderRadius:10,marginBottom:8,borderWidth:1,borderColor:'#E2E8F0'},
  checkRow:    {flexDirection:'row',alignItems:'flex-start',gap:10,marginBottom:14},
  checkLabel:  {flex:1,fontSize:13,color:'#475569',lineHeight:18},
  navBar:      {flexDirection:'row',alignItems:'center',justifyContent:'space-between',paddingHorizontal:16,paddingVertical:12,backgroundColor:'#FFF',borderTopWidth:1,borderTopColor:'#E2E8F0'},
  navStep:     {fontSize:12,fontWeight:'600',color:'#94A3B8'},
  prevBtn:     {flexDirection:'row',alignItems:'center',paddingHorizontal:14,paddingVertical:10,borderRadius:10,borderWidth:1,borderColor:'#0e4e96'},
  prevBtnT:    {fontSize:14,fontWeight:'600',color:'#0e4e96',marginLeft:2},
  nextBtn:     {flexDirection:'row',alignItems:'center',backgroundColor:'#0e4e96',paddingHorizontal:18,paddingVertical:10,borderRadius:10},
  nextBtnT:    {fontSize:14,fontWeight:'700',color:'#FFF',marginRight:2},
  modalHdr:    {flexDirection:'row',alignItems:'center',justifyContent:'space-between',padding:16,borderBottomWidth:1,borderBottomColor:'#E2E8F0'},
  submitBtn:   {flexDirection:'row',alignItems:'center',justifyContent:'center',backgroundColor:'#10b981',paddingVertical:14,borderRadius:12,gap:8},
  submitBtnT:  {fontSize:15,fontWeight:'700',color:'#FFF'},
  editBtn:     {flexDirection:'row',alignItems:'center',justifyContent:'center',paddingVertical:12,borderRadius:12,borderWidth:1,borderColor:'#E2E8F0'},
  editBtnT:    {fontSize:14,fontWeight:'600',color:'#0e4e96'},
  successOverlay:{flex:1,backgroundColor:'rgba(0,0,0,0.5)',justifyContent:'center',alignItems:'center'},
  successCard: {backgroundColor:'#FFF',borderRadius:20,padding:32,alignItems:'center',marginHorizontal:30,width:SW-60},
});
